import { getCountdownUnitsFromSeconds } from '@amusnet/react-ui'
import { CountdownCounterDisplayItems, CountdownCounterValues } from '@components/CountdownFreeSpinsBonus/types'
import { BonusImages } from '@constants/bonusSystem'
import { Bonus, BonusTypes, BuyBonusFeature } from 'game-ui-types'

const getBonusProp = <K extends keyof BuyBonusFeature>(
    bonus: Bonus,
    features: BuyBonusFeature[],
    prop: K
): BuyBonusFeature[K] | '' => {
    const bonusLabel = bonus.typeConfiguration?.freeBuyBonusConfiguration?.featureId

    if (!bonusLabel) {
        return ''
    }

    const feature = features.find((f) => f.guaranteedName === bonusLabel || f.name === bonusLabel)

    return feature ? feature[prop] : ''
}

export const getFBBDetails = (
    bonus: Bonus,
    features: BuyBonusFeature[]
):
    | {
          label: string
          name: string
          imageName: string
          multiplier: number | string
          guaranteedName: string | undefined
          guaranteedMultiplier: number | string | undefined
      }
    | undefined => {
    if (!bonus.typeConfiguration?.freeBuyBonusConfiguration) {
        return
    }

    const label = getBonusProp(bonus, features, 'label')
    const name = getBonusProp(bonus, features, 'name')
    const multiplier = getBonusProp(bonus, features, 'multiplier')
    const guaranteedName = getBonusProp(bonus, features, 'guaranteedName')
    const guaranteedMultiplier = getBonusProp(bonus, features, 'guaranteedMultiplier')
    const featureIndex = features.findIndex((f) => f.label === label)
    const imageName =
        features.length > 1 ? `free-buy-bonus-image-card-0${featureIndex + 1}.png` : BonusImages.FREE_BUY_BONUS_MAIN

    return {
        label,
        name,
        imageName,
        multiplier,
        guaranteedName,
        guaranteedMultiplier,
    }
}

export const modifyFreeBuyBonuses = (bonuses: Bonus[], bonusFeatures: BuyBonusFeature[]): Bonus[] => {
    const freeBuyBonuses = (bonuses as Bonus[]).filter((bonus) => bonus.type === BonusTypes.FREE_BUY_BONUS)

    for (const bonus of freeBuyBonuses) {
        const config = bonus.typeConfiguration?.freeBuyBonusConfiguration

        if (!config) continue

        const details = getFBBDetails(bonus, bonusFeatures)

        if (!details) continue

        config.label = details.label
        config.imageName = details.imageName
        config.multiplier = details.multiplier
        config.guaranteedName = details.guaranteedName
        config.guaranteedMultiplier = details.guaranteedMultiplier
    }

    return freeBuyBonuses
}

export const getCountdownValuesFromBonus = (
    bonus?: Bonus,
    forceUseSecondsFromConfig = false
): CountdownCounterValues => {
    if (!bonus || bonus.type !== BonusTypes.COUNTDOWN_FREE_SPIN) return null

    let totalSeconds = Number(bonus.typeConfiguration?.countdownFreeSpinConfiguration?.countdownSeconds)

    if (!forceUseSecondsFromConfig && bonus.expirationTime) {
        const totalMilliseconds = +bonus.expirationTime - Date.now()

        totalSeconds = totalMilliseconds / 1000
    }

    if (!totalSeconds || totalSeconds <= 0) return null

    const { days, hours, minutes, seconds } = getCountdownUnitsFromSeconds(totalSeconds)

    if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) return null

    return { days, hours, minutes, seconds }
}

/**
 * Formats a 4-items countdown array into two display pairs.
 * Automatically identifies the highest active unit and ensures a
 * two-part string output (e.g., "5 Days: 2 Hours" or "0 Min: 45 Sec").
 */
export const getCountdownDisplayItems = (items: CountdownCounterDisplayItems) => {
    const firstActiveIndex = items.findIndex((u) => u.val > 0)

    const startIndex = firstActiveIndex === -1 ? 2 : Math.min(firstActiveIndex, 2)
    const endIndex = startIndex + 1

    const firstStringDigits = `${items[startIndex].val ?? 0}`
    const firstStringLabel = ` ${items[startIndex].label} : `
    // Add 0 padding only for seconds
    const secondStringDigits =
        startIndex !== 2 ? `${items[endIndex].val ?? 0}` : `0${items[endIndex].val ?? 0}`.slice(-2)
    const secondStringLabel = ` ${items[endIndex].label}`

    return { firstStringDigits, firstStringLabel, secondStringDigits, secondStringLabel }
}
